import usePopups from "@/stores/popupsStore"
import { faChevronLeft, faChevronRight, faEllipsis } from "@fortawesome/free-solid-svg-icons"
import { css } from "vite-css-in-js"
import { computed } from "vue"
import { Deferred, defineComponent, inferProps, ModalTitle, type ReactiveComponent } from "vue-utils"
import BootstrapButton from "../form/BootstrapButton"
import Icon from "../Icon"
import SelectPageForm from "./SelectPageForm"

interface Props {
	page: number
	totalPages: number
	pageSize?: number
	setPage(page: number): void
}

const pagingStyles = css`
	display: inline-flex;
	list-style: none;
	gap: 0.5rem;

	button {
		display: flex;
		align-items: center;
		justify-content: center;
		user-select: none;
		text-align: center;
		min-width: 2rem;
		height: 2rem;
		padding: 0 0.125rem;
		transition: none;

		&:disabled {
			cursor: not-allowed;
			pointer-events: all;
		}
	}
`

const Pagination: ReactiveComponent<Props> = (props) => {
	const popups = usePopups()

	const pageButtons = computed(() => {
		const listSize = props.pageSize ?? 9
		const result: number[] = []
		for (let i = 0; i < props.totalPages * 2 && result.length < listSize; i++) {
			const page = i % 2 === 0 ? props.page + i / 2 : props.page - (i + 1) / 2
			if (page < 1 || page > props.totalPages) {
				continue
			}
			result.push(page)
		}
		result.sort((a, b) => a - b)
		if (result.length > 2 && result[0] !== 1) {
			result.shift()
			result.shift()
		}
		if (result.length > 2 && result[result.length - 1] !== props.totalPages) {
			result.pop()
			result.pop()
		}
		return result
	})

	function renderButton(page: number) {
		return (
			<BootstrapButton
				style={{ fontSize: "1.25rem" }}
				color={props.page === page ? "primary" : "light"}
				onClick={() => props.setPage(page)}
			>
				{page}
			</BootstrapButton>
		)
	}

	async function handleSelectPageClick() {
		const deferred = new Deferred<number | null>()
		popups.openPopup({
			width: "24rem",
			content: (popupProps) => (
				<>
					<ModalTitle title="Enter Page Number" />
					<hr />
					<SelectPageForm
						totalPages={props.totalPages}
						cancel={() => {
							popupProps.close()
							deferred.resolve(null)
						}}
						select={(page) => {
							popupProps.close()
							deferred.resolve(page)
						}}
					/>
				</>
			),
		})

		const selectedPage = await deferred
		if (selectedPage && selectedPage >= 1 && selectedPage <= props.totalPages) {
			props.setPage(selectedPage)
		}
	}

	return () => (
		<ul class={pagingStyles}>
			<BootstrapButton color="secondary" onClick={() => props.setPage(props.page - 1)} disabled={props.page === 1}>
				<Icon icon={faChevronLeft} />
			</BootstrapButton>
			{props.totalPages > 1 && !pageButtons.value.includes(1) && (
				<>
					<li>{renderButton(1)}</li>
					<li>
						<BootstrapButton
							color="light"
							title="Navigate to specific page"
							onClick={() => void handleSelectPageClick()}
						>
							<Icon icon={faEllipsis} />
						</BootstrapButton>
					</li>
				</>
			)}
			{pageButtons.value.map((p) => (
				<li key={p}>{renderButton(p)}</li>
			))}
			{props.totalPages > 1 && !pageButtons.value.includes(props.totalPages) && (
				<>
					<li>
						<BootstrapButton
							color="light"
							title="Navigate to specific page"
							onClick={() => void handleSelectPageClick()}
						>
							<Icon icon={faEllipsis} />
						</BootstrapButton>
					</li>
					<li>{renderButton(props.totalPages)}</li>
				</>
			)}
			<BootstrapButton
				color="secondary"
				onClick={() => props.setPage(props.page + 1)}
				disabled={props.page === props.totalPages}
			>
				<Icon icon={faChevronRight} />
			</BootstrapButton>
		</ul>
	)
}

export default defineComponent(Pagination, inferProps<Props>())
